import React, { useMemo } from 'react';
import { Skeleton, Tag } from '@innovationnorway/cl-react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageParams } from '../../types/PageParams';
import { OneWayInStatus } from 'src/api/v2';
import { determineTagType, translateStatusTag } from '../../../opportunity-table/helpers/statusTags';
import { statusSort } from '../../../utils/statusSort';
import ReadMore from 'src/components/ReadMore/ReadMore';
import InfoBox from './InfoBox';
import useActorsWithAccess from '../../hooks/use-actors-with-access';

const ActorsWithAccess = () => {
  const { formId } = useParams<PageParams>();
  const { t: tOneWayIn } = useTranslation('oneWayIn');

  const { actors, isLoading } = useActorsWithAccess(formId ?? '');

  const sortedActors = useMemo(
    () => [...(actors ?? [])].sort((a, b) => statusSort(a.status, b.status, true)),
    [actors],
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="display--flex flex-direction--column gap--ds-2">
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </div>
      );
    }

    return (
      <ReadMore maxHeight="var(--opportunity-overview-collapsed-height)">
        <div className="display--flex flex-direction--column gap--ds-2">
          {sortedActors.map((actor) => (
            <ActorsWithAccessRow key={actor.clusterId} actorName={actor.clusterName} status={actor.status} />
          ))}
        </div>
      </ReadMore>
    );
  };

  return <InfoBox heading={tOneWayIn('opportunityDetails.ActorsWithAccess')}>{renderContent()}</InfoBox>;
};

const ActorsWithAccessRow = ({ actorName, status }: { actorName: string; status: OneWayInStatus }) => {
  return (
    <div className="display--flex flex-direction--row align-items--center justify-content--space-between gap--1">
      {actorName}
      <Tag data-color={determineTagType(status)}>{translateStatusTag(status)}</Tag>
    </div>
  );
};

export default ActorsWithAccess;
